import { useCallback, useEffect, useRef, useState } from 'react';

import {
  accountSecurityService,
  RESEND_LINK_PAUSE_SECONDS,
  sendLinkFailure,
} from '@personal-trainer-app/shared';
import {
  dayjs,
  formatDisplayDate,
} from '@personal-trainer-app/shared/src/utils/date';

import { useResendCooldown } from '../useResendCooldown';

import type {
  SendLinkFailure,
  VerificationLinkResult,
} from '@personal-trainer-app/shared';

import { useAuthStore } from '@/stores';

export interface UseSendVerificationLinkReturn {
  /**
   * Ask for a new link. Resolves `sent`, `already-verified` once the verified
   * user has been read, or null when the ask failed (`failure` says why) or
   * one was already on its way.
   */
  send: () => Promise<VerificationLinkResult | null>;
  /** Whether an ask is on its way. */
  sending: boolean;
  /** Why the last ask failed, until the next one. */
  failure: SendLinkFailure | null;
  /** Forget why the last ask failed, as another action takes its place. */
  clearFailure: () => void;
  /** When the last link was sent, as a clock time; null before one is. */
  sentAt: string | null;
  /** Seconds until another may be asked for. */
  remaining: number;
}

/**
 * Asking for a new verification link, for the `/verify-email` page and the
 * unverified gate: the call, the pause after it, and why it failed. An
 * account that turns out to be verified already is read again, so the store
 * has the verified user and the gate lifts.
 */
export function useSendVerificationLink(): UseSendVerificationLinkReturn {
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<SendLinkFailure | null>(null);
  const [sentAt, setSentAt] = useState<string | null>(null);
  const { remaining, start } = useResendCooldown(RESEND_LINK_PAUSE_SECONDS);
  // One ask at a time, whatever the last render saw.
  const inFlight = useRef(false);
  // A verified account lifts the gate, which unmounts before the ask ends.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const send = useCallback(async (): Promise<VerificationLinkResult | null> => {
    if (inFlight.current) return null;
    inFlight.current = true;
    setSending(true);
    setFailure(null);
    try {
      const result = await accountSecurityService.sendVerificationLink();
      if (result === 'already-verified') {
        await useAuthStore.getState().fetchUser();
      } else if (mounted.current) {
        start();
        setSentAt(formatDisplayDate(dayjs(), 'time'));
      }
      return result;
    } catch (error) {
      if (mounted.current) setFailure(sendLinkFailure(error));
      return null;
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  }, [start]);

  const clearFailure = useCallback((): void => setFailure(null), []);

  return { send, sending, failure, clearFailure, sentAt, remaining };
}
